/**
 * The printable order receipt, used as a packing slip.
 *
 * Built as a standalone HTML document rather than a server-rendered PDF: this is printed, and every browser
 * turns a print dialog into a PDF. A generated PDF would mean a layout library in the Lambda bundle and a
 * second place for the order to be formatted, for a file the operator gets either way.
 *
 * The builder is pure so the arithmetic can be tested — a packing slip that disagrees with what the customer
 * was charged is worse than no packing slip.
 */

export interface ReceiptLine {
  name: string;
  quantity: number;
  unitCents: number;
}

/** Every optional accepts `undefined` explicitly: callers pass their own order types straight in, and
 *  `exactOptionalPropertyTypes` otherwise rejects a declared-but-possibly-undefined field. */
export interface ReceiptAddress {
  name?: string | undefined; phone?: string | undefined; line1?: string | undefined; line2?: string | undefined;
  city?: string | undefined; state?: string | undefined; postalCode?: string | undefined;
  country?: string | undefined;
}

export interface ReceiptOrder {
  orderNum: string;
  status?: string | undefined;
  createdOn?: string | undefined;
  contactEmail?: string | undefined;
  items: ReceiptLine[];
  subtotalCents: number;
  shippingCents: number;
  taxCents?: number | undefined;
  discountCents?: number | undefined;
  promotionCode?: string | undefined;
  totalCents: number;
  shippingCarrier?: string | undefined;
  shippingService?: string | undefined;
  shippingAddress?: ReceiptAddress | undefined;
  tracking?: { carrier: string; trackingNumber: string; shippedOn?: string | undefined } | undefined;
}

const money = (cents: number) =>
  `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const esc = (s: string) =>
  String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/** Ship-to as display lines, blanks dropped so an incomplete address does not print as gaps. */
export function addressLines(a: ReceiptAddress | undefined): string[] {
  if (!a) return [];
  const cityLine = [a.city, a.state].filter(Boolean).join(', ');
  return [
    a.name,
    a.line1,
    a.line2,
    [cityLine, a.postalCode].filter(Boolean).join(' '),
    a.country && a.country.toUpperCase() !== 'US' ? a.country : undefined,
    a.phone,
  ]
    .map((v) => (v ?? '').trim())
    .filter(Boolean);
}

/**
 * The money rows, in the order they are read.
 *
 * A row is omitted when it is zero rather than printed as $0.00 — except the TOTAL, which always prints
 * because a receipt without one is not a receipt. The discount is shown as a negative so the arithmetic
 * on the page visibly reconciles: items − discount + shipping + tax = total.
 */
export function totalRows(order: ReceiptOrder): { label: string; value: string; strong?: boolean }[] {
  const rows: { label: string; value: string; strong?: boolean }[] = [
    { label: 'Subtotal', value: money(order.subtotalCents) },
  ];
  const discount = order.discountCents ?? 0;
  if (discount > 0) {
    rows.push({
      label: order.promotionCode ? `Discount (${order.promotionCode})` : 'Discount',
      value: `−${money(discount)}`,
    });
  }
  if (order.shippingCents) rows.push({ label: 'Shipping', value: money(order.shippingCents) });
  if (order.taxCents) rows.push({ label: 'Tax', value: money(order.taxCents) });
  rows.push({ label: 'Total', value: money(order.totalCents), strong: true });
  return rows;
}

/** `LAST_FIRST`-style filename stem for the saved PDF, so a folder of them is sortable and identifiable. */
export const receiptFilename = (order: ReceiptOrder) => `EFTCreator-${order.orderNum}-receipt`;

/**
 * A complete, self-contained HTML document.
 *
 * Everything is inline — no stylesheet, no image, no font — because this is opened in a blank window and
 * printed immediately; anything fetched might not arrive before the print dialog does, and would leave a
 * half-drawn packing slip in the box.
 */
export function receiptHtml(order: ReceiptOrder, opts?: { logoUrl?: string }): string {
  const placed = order.createdOn
    ? new Date(order.createdOn).toLocaleString('en-US', {
        year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit',
      })
    : '';
  const ship = addressLines(order.shippingAddress);
  const rows = order.items
    .map(
      (li) =>
        `<tr><td>${esc(li.name)}</td><td class="c">${li.quantity}</td>` +
        `<td class="r">${money(li.unitCents)}</td><td class="r">${money(li.unitCents * li.quantity)}</td></tr>`,
    )
    .join('');
  const totals = totalRows(order)
    .map(
      (t) =>
        `<tr class="${t.strong ? 'tot' : ''}"><td colspan="3" class="r">${esc(t.label)}</td>` +
        `<td class="r">${esc(t.value)}</td></tr>`,
    )
    .join('');

  return `<!doctype html><html><head><meta charset="utf-8"><title>${esc(receiptFilename(order))}</title>
<style>
  @page { size: letter; margin: 16mm; }
  * { box-sizing: border-box; }
  body { font-family: Arial, Helvetica, sans-serif; color: #222; font-size: 12pt; margin: 0; }
  .head { display: flex; justify-content: space-between; align-items: flex-start;
          border-bottom: 3px solid #55B848; padding-bottom: 12px; margin-bottom: 18px; }
  .brand { font-size: 20pt; font-weight: bold; letter-spacing: -0.5px; }
  .brand span { color: #55B848; }
  .meta { text-align: right; font-size: 10.5pt; line-height: 1.5; }
  .meta .num { font-family: monospace; font-size: 13pt; font-weight: bold; }
  h2 { font-size: 11pt; text-transform: uppercase; letter-spacing: 0.6px; color: #777;
       margin: 0 0 6px; }
  .cols { display: flex; gap: 40px; margin-bottom: 22px; }
  .col { flex: 1; }
  .addr { line-height: 1.5; }
  table { width: 100%; border-collapse: collapse; }
  th { text-align: left; font-size: 10pt; text-transform: uppercase; letter-spacing: 0.5px;
       color: #777; border-bottom: 1.5px solid #ddd; padding: 6px 8px; }
  td { padding: 7px 8px; border-bottom: 1px solid #eee; }
  td.c { text-align: center; } td.r { text-align: right; }
  tr.tot td { border-top: 2px solid #333; border-bottom: none; font-weight: bold; font-size: 13pt;
              padding-top: 10px; }
  .foot { margin-top: 26px; padding-top: 12px; border-top: 1px solid #eee;
          font-size: 10pt; color: #777; text-align: center; line-height: 1.6; }
  /* Never let the item table split a row across pages — half a line item is unreadable on a slip. */
  tr { break-inside: avoid; }
</style></head><body>
  <div class="head">
    <div>
      ${opts?.logoUrl
        ? `<img src="${esc(opts.logoUrl)}" alt="EFTCreator" style="height:38px">`
        : '<div class="brand">EFT<span>Creator</span></div>'}
      <div style="font-size:10pt;color:#777;margin-top:4px;">Lakota Software Solutions, Inc.</div>
    </div>
    <div class="meta">
      <div class="num">${esc(order.orderNum)}</div>
      ${placed ? `<div>${esc(placed)}</div>` : ''}
      ${order.status ? `<div>${esc(order.status)}</div>` : ''}
    </div>
  </div>

  <div class="cols">
    <div class="col">
      <h2>Ship to</h2>
      <div class="addr">${ship.length ? ship.map(esc).join('<br>') : '&mdash;'}</div>
    </div>
    <div class="col">
      <h2>Order details</h2>
      <div class="addr">
        ${order.contactEmail ? `${esc(order.contactEmail)}<br>` : ''}
        ${order.shippingService ? `${esc(order.shippingService)}<br>` : ''}
        ${order.tracking
          ? `${esc(order.tracking.carrier)} ${esc(order.tracking.trackingNumber)}`
          : ''}
      </div>
    </div>
  </div>

  <table>
    <thead><tr><th>Item</th><th style="text-align:center">Qty</th>
      <th style="text-align:right">Unit</th><th style="text-align:right">Amount</th></tr></thead>
    <tbody>${rows || '<tr><td colspan="4">No items</td></tr>'}${totals}</tbody>
  </table>

  <div class="foot">
    Thank you for your order.<br>
    Questions? <strong>support@lakotasoftware.com</strong>
  </div>
</body></html>`;
}

/**
 * Open the receipt in a new tab and raise the print dialog, where the browser's own "Save as PDF" produces
 * the file. Returns false when the tab was blocked, so the caller can say so rather than appearing to do
 * nothing — a popup blocker is the one failure a user cannot diagnose from an unchanged screen.
 *
 * `onafterprint` closes the tab once the dialog is dismissed, so a session of printing slips does not leave
 * a trail of windows. Browsers that do not fire it simply leave the tab open, which is harmless.
 */
export function printReceipt(order: ReceiptOrder, opts?: { logoUrl?: string }): boolean {
  /*
   * NO `noopener` here, however much it looks like it belongs.
   *
   * Passing it makes `window.open` return NULL by specification — severing the opener relationship means
   * there is no handle to hand back. The window still opens, so the first version of this produced exactly
   * the worst pair of symptoms: a blank pop-up AND a "pop-ups are blocked" message, with no blocker
   * involved and nothing ever written into the window.
   *
   * Safe to omit: the document is our own HTML, written by us into a blank same-origin window. `noopener`
   * exists to stop an UNTRUSTED destination reaching back through `window.opener`, and there is no
   * untrusted destination here.
   */
  const w = window.open('', '_blank', 'width=900,height=1000');
  if (!w) return false;
  w.document.write(receiptHtml(order, opts));
  w.document.close();
  // `focus` before `print`: a background tab's dialog can open behind the window that spawned it.
  w.focus();
  w.onafterprint = () => w.close();
  // The document is fully inline, so there is nothing to wait for beyond the parse that close() completes.
  w.print();
  return true;
}
